import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Compass, ListOrdered } from 'lucide-react';

import type { Tour } from '@/config/objects/tours';
import { cn } from '@/lib/utils';

type PathMode = 'defined' | 'fastest';

interface TourPathModeSelectorProps {
  tourId: string;
  /**
   * The tour's current pathMode. May be undefined while the tour is still
   * loading — the component renders both options as inactive in that case.
   */
  current?: PathMode;
}

interface TourQueryShape {
  success: boolean;
  data: Tour;
}

interface ModeCardProps {
  active: boolean;
  pending: boolean;
  title: string;
  description: string;
  Icon: typeof Compass;
  onSelect: () => void;
}

function ModeCard({ active, pending, title, description, Icon, onSelect }: ModeCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      disabled={pending}
      className={cn(
        'flex flex-1 items-start gap-3 rounded-xl border p-4 text-left transition-colors',
        active
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border bg-card hover:bg-accent',
        pending && 'opacity-60',
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {active && <Check className="h-4 w-4 text-primary" aria-label="Active" />}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

/**
 * Inline picker that lets the admin switch a tour between "Fastest" and
 * "Defined route" modes without leaving the Attractions tab. Mirrors the
 * field on the tour edit form — both write the same `pathMode` column.
 */
export function TourPathModeSelector({ tourId, current }: TourPathModeSelectorProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = ['Tour', tourId] as const;

  const mutation = useMutation<
    void,
    Error,
    PathMode,
    { previous: TourQueryShape | undefined }
  >({
    mutationFn: async (mode) => {
      await apiClient.patch(`/tours/${tourId}`, { pathMode: mode });
    },
    // Optimistically flip the cached pathMode so the toggle shows the new
    // mode the instant the user clicks. If the server rejects, we restore
    // from the snapshot in onError. onSettled then refetches as the source
    // of truth.
    onMutate: async (mode) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TourQueryShape>(queryKey);
      if (previous?.data) {
        queryClient.setQueryData<TourQueryShape>(queryKey, {
          ...previous,
          data: { ...previous.data, pathMode: mode },
        });
      }
      return { previous };
    },
    // No onSuccess toast — the optimistic flip already gave the user
    // immediate feedback. A toast on top would be redundant.
    onError: (_error, _mode, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error('Failed to change route mode');
      // Refetch so we converge on whatever the server actually has,
      // in case the failure left the cache in an odd state.
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Prefer the in-flight optimistic value so the toggle never lags behind a
  // click. Once the request settles, `current` becomes the source of truth.
  const effective: PathMode | undefined =
    mutation.isPending && mutation.variables ? mutation.variables : current;

  const select = (mode: PathMode) => {
    if (mode === effective) return;
    mutation.mutate(mode);
  };

  return (
    <section
      role="radiogroup"
      aria-label="Tour route mode"
      className="rounded-xl border bg-card p-4 shadow-sm"
    >
      <header className="mb-3">
        <h2 className="text-sm font-semibold">Route mode</h2>
        <p className="text-xs text-muted-foreground">
          How mobile users walk this tour. Switch anytime — the saved order
          below is preserved either way.
        </p>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row">
        <ModeCard
          active={effective === 'fastest'}
          pending={mutation.isPending}
          title="Fastest"
          description="Sends each guest to their closest unvisited stop based on their current location."
          Icon={Compass}
          onSelect={() => select('fastest')}
        />
        <ModeCard
          active={effective === 'defined'}
          pending={mutation.isPending}
          title="Defined route"
          description="Walks the tour in the exact order you set below. Guests can still opt out and use proximity."
          Icon={ListOrdered}
          onSelect={() => select('defined')}
        />
      </div>
    </section>
  );
}
